import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Section, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';

interface Coverage {
  locales: Array<{
    locale: string;
    name: string;
    rtl: boolean;
    ui: { total: number; missing: number; missingKeys: string[] };
    help: { total: number; missing: number };
  }>;
}

/**
 * Çeviriler (F7-17): dil başına arayüz ve yardım metinlerinin kapsamı. Kaynak dil Türkçe; eksik metin İngilizceye,
 * o da yoksa Türkçeye düşer. Sayılar paketle gelen kataloglardan hesaplanır (CI'daki eksik anahtar raporuyla aynı).
 */
export function UiTranslationsPage() {
  const t = useT();
  const q = useQuery({
    queryKey: ['ui-translations'],
    queryFn: () => api.get<Coverage>('/v1/admin/translations/coverage'),
  });
  const count = (missing: number, total: number) =>
    missing === 0 ? (
      <Badge tone="moss">{t('admin.uiTranslations.complete', { total })}</Badge>
    ) : (
      <Badge tone="amber">{t('admin.uiTranslations.missing', { count: missing, total })}</Badge>
    );
  return (
    <PageFrame helpKey="page.ui_translations">
      <Section
        title={t('admin.uiTranslations.coverageTitle')}
        description={t('admin.uiTranslations.coverageLead')}
        action={<HelpTip k="ui_translations.coverage" />}
      >
        {!q.data ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.uiTranslations.coverageTitle')}>
              <thead className="bg-surface-2 text-start text-ink-2">
                <tr>
                  <th scope="col" className="px-4 py-2 text-start font-medium">
                    {t('admin.uiTranslations.language')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-start font-medium">
                    {t('admin.uiTranslations.ui')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-start font-medium">
                    {t('admin.uiTranslations.help')}
                  </th>
                  <th scope="col" className="px-4 py-2 text-start font-medium">
                    {t('admin.uiTranslations.direction')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {q.data.locales.map((l) => (
                  <tr key={l.locale}>
                    <th scope="row" className="px-4 py-2.5 text-start font-medium" lang={l.locale}>
                      {l.name} <span className="font-normal text-ink-3">({l.locale})</span>
                    </th>
                    <td className="px-4 py-2.5">{count(l.ui.missing, l.ui.total)}</td>
                    <td className="px-4 py-2.5">{count(l.help.missing, l.help.total)}</td>
                    <td className="px-4 py-2.5">
                      {l.rtl ? t('admin.uiTranslations.rtl') : t('admin.uiTranslations.ltr')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {q.data?.locales
          .filter((l) => l.ui.missing > 0)
          .map((l) => (
            <details key={l.locale} className="mt-3 rounded-md border border-line px-4 py-2 text-sm">
              <summary className="cursor-pointer font-medium">
                {t('admin.uiTranslations.missingKeys', { language: l.name, count: l.ui.missing })}
              </summary>
              <ul className="mt-2 space-y-1 font-mono text-[0.8125rem] text-ink-2">
                {l.ui.missingKeys.map((k) => (
                  <li key={k}>{k}</li>
                ))}
              </ul>
            </details>
          ))}
        <p className="mt-4 text-sm text-ink-3">
          {t('admin.uiTranslations.helpNote')}{' '}
          <Link to="/yardim-metinleri" className="text-pine underline underline-offset-2">
            {t('admin.nav.helpEditor')}
          </Link>
        </p>
      </Section>
    </PageFrame>
  );
}
